import { useEffect, useRef } from "react";
import { subscribe, LeadBusEvent } from "../Pages/LeadSystem/leadBus";

/* ──────────────────────────────────────────────────────────────────────
   useLeadBus — clean subscription to the global lead event bus.

   Wraps `leadBus.subscribe()` with React lifecycle: registers on mount,
   unregisters on unmount. The handler ref pattern means callers don't
   need to wrap their handler in useCallback — the most recent handler
   is always invoked even if the function identity changes between
   renders.

   Usage:
     useLeadBus((ev) => {
       if (ev.type === "lead.updated" && ev.leadId === lead.id) {
         loadAll();
       }
     });

   Or to listen for everything and refetch the surface:
     useLeadBus(() => loadData());

   Mutations made from THIS surface still trigger the event — but
   `source` is set to this tab so the cross-tab transport doesn't
   echo it back. The same-tab in-process broadcast does fire; if
   that creates a refetch loop, surfaces can filter on `ev.leadId`
   or just accept the extra refresh (it idempotently fetches the
   same data and is rate-limited by the network).
   ────────────────────────────────────────────────────────────────────── */
export function useLeadBus(handler: (event: LeadBusEvent) => void): void {
  // Keep the latest handler in a ref so we don't have to re-subscribe
  // on every render — subscribe only on mount.
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; }, [handler]);

  useEffect(() => {
    const unsub = subscribe((ev) => handlerRef.current(ev));
    return unsub;
  }, []);
}
